/**
 * Os suportes dos itens de ângulos de polígonos, como funções PURAS.
 *
 *  - `exploradorSomaInternos(n)`: um polígono de n lados dividido em triângulos pelas
 *    diagonais de UM vértice, cada triângulo com os seus 180°.
 *  - `exploradorAnguloInterno(n)`: um polígono REGULAR de n lados com os ângulos
 *    internos marcados, a soma e a medida de cada um.
 *  - `animacaoExternos(t)`: triângulo, quadrilátero e pentágono lado a lado. Em cada
 *    vértice, o lado é prolongado e o giro até o lado seguinte ganha um setor. Depois os
 *    três ENCOLHEM, com os setores do mesmo tamanho, até virarem um ponto: os setores de
 *    cada um fecham um círculo inteiro. Três polígonos diferentes, o mesmo círculo — é o
 *    que o vídeo do GeoGebra mostrava.
 *
 * O ENCOLHIMENTO VAI ATÉ ZERO. Numa versão anterior ele parava em 3% do tamanho, e os
 * setores, ainda presos a vértices ligeiramente separados, deixavam um vão visível na
 * junção. Com a escala zero, os setores ficam com o mesmo centro, e o ângulo final de
 * cada um é, por construção, o inicial do seguinte: o círculo fecha sem emenda.
 */

import { formatBR } from '../core/num.ts';
import { conta } from '../core/pt.ts';
import type { Vec2 } from '../geom/vec.ts';
import type { Scene, Shape } from './scene.ts';
import { scene } from './scene.ts';

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));
const suave = (x: number): number => {
  const u = clamp01(x);
  return u * u * (3 - 2 * u);
};

const g = (x: number): string => `${formatBR(x, 0)}°`;
/** Graus com uma casa quando não são inteiros: 128,6°. */
const g1 = (x: number): string =>
  Math.abs(x - Math.round(x)) < 1e-9 ? g(x) : `${formatBR(x, 1)}°`;

/** Desvio angular de cada vértice, em frações do passo 2π/n: tira a regularidade. */
const DESVIOS = [0.0, 0.16, -0.1, 0.12, -0.14, 0.08, -0.06, 0.1];

/** Polígono convexo irregular: vértices num círculo, com espaçamentos desiguais. */
export function poligonoIrregular(n: number, R: number, centro: Vec2 = { x: 0, y: 0 }): Vec2[] {
  const passo = (2 * Math.PI) / n;
  return Array.from({ length: n }, (_v, k) => {
    const a = Math.PI / 2 + k * passo + (DESVIOS[k % DESVIOS.length] as number) * passo;
    return { x: centro.x + R * Math.cos(a), y: centro.y + R * Math.sin(a) };
  });
}

export function poligonoRegular(n: number, R: number): Vec2[] {
  // Um lado na base: o polígono "apoiado", como se desenha no caderno.
  const inicio = -Math.PI / 2 - Math.PI / n;
  return Array.from({ length: n }, (_v, k) => {
    const a = inicio + (2 * Math.PI * k) / n;
    return { x: R * Math.cos(a), y: R * Math.sin(a) };
  });
}

const centroide = (vs: readonly Vec2[]): Vec2 => ({
  x: vs.reduce((s, v) => s + v.x, 0) / vs.length,
  y: vs.reduce((s, v) => s + v.y, 0) / vs.length,
});

function titulo(texto: string, y: number): Shape {
  return {
    t: 'label',
    at: { x: 0, y },
    text: [{ t: 'text', s: texto }],
    anchor: 'c',
    emphasis: 'title',
  };
}

const R_EXPLORADOR = 100;
const QUADRO_EXPLORADOR = { x: -170, y: -150, w: 340, h: 300 };

// ---------------------------------------------------------------------------
// Explorador: soma dos ângulos internos
// ---------------------------------------------------------------------------

export function exploradorSomaInternos(n: number, a11y: { title: string; desc: string }): Scene {
  const vs = poligonoIrregular(n, R_EXPLORADOR);
  const T = n - 2;
  const formas: Shape[] = [];
  for (let j = 0; j < T; j++) {
    const a = vs[0] as Vec2;
    const b = vs[j + 1] as Vec2;
    const c = vs[j + 2] as Vec2;
    formas.push({
      t: 'poly',
      pts: [a, b, c],
      closed: true,
      role: 'construction',
      fill: j % 2 === 0 ? 'result-fill' : 'given-fill',
    });
    formas.push({
      t: 'label',
      at: centroide([a, b, c]),
      text: [{ t: 'text', s: '180°' }],
      anchor: 'c',
      emphasis: 'tick',
    });
  }
  formas.push({ t: 'poly', pts: vs, closed: true, role: 'given' });
  vs.forEach((v, k) => formas.push({ t: 'point', p: v, role: k === 0 ? 'result' : 'given' }));
  formas.push(titulo(`${conta(n, 'lado')} → ${conta(T, 'triangulo')}`, 132));
  formas.push(titulo(`Soma dos internos = ${T} × 180° = ${g(T * 180)}`, -135));
  return scene(formas, { a11y, viewBox: QUADRO_EXPLORADOR });
}

// ---------------------------------------------------------------------------
// Explorador: ângulo interno do polígono regular
// ---------------------------------------------------------------------------

export function exploradorAnguloInterno(n: number, a11y: { title: string; desc: string }): Scene {
  const vs = poligonoRegular(n, R_EXPLORADOR);
  const soma = (n - 2) * 180;
  const cada = soma / n;
  const formas: Shape[] = [{ t: 'poly', pts: vs, closed: true, role: 'given', fill: 'given-fill' }];
  // SETORES PEQUENOS, dentro de cada vértice. A marca de ângulo genérica escolhia o raio
  // pelo tamanho da figura, e os arcos, grandes demais, emendavam uns nos outros.
  const raio = R_EXPLORADOR * 0.24;
  vs.forEach((v, k) => {
    const antes = vs[(k - 1 + n) % n] as Vec2;
    const depois = vs[(k + 1) % n] as Vec2;
    // Polígono no sentido anti-horário: o interno vai do raio para o vértice seguinte,
    // girando no mesmo sentido, até o raio para o anterior.
    const a0 = Math.atan2(depois.y - v.y, depois.x - v.x);
    let a1 = Math.atan2(antes.y - v.y, antes.x - v.x);
    while (a1 < a0) a1 += 2 * Math.PI;
    const pts: Vec2[] = [v];
    for (let i = 0; i <= 12; i++) {
      const a = a0 + ((a1 - a0) * i) / 12;
      pts.push({ x: v.x + raio * Math.cos(a), y: v.y + raio * Math.sin(a) });
    }
    formas.push({ t: 'poly', pts, closed: true, role: 'result', fill: 'result-fill' });
    if (k === 0) {
      // O valor vai num vértice só: repetido em todos, ele poluía a figura.
      const am = (a0 + a1) / 2;
      formas.push({
        t: 'label',
        at: {
          x: v.x + R_EXPLORADOR * 0.5 * Math.cos(am),
          y: v.y + R_EXPLORADOR * 0.5 * Math.sin(am),
        },
        text: [{ t: 'text', s: g1(cada) }],
        anchor: 'c',
        emphasis: 'tick',
      });
    }
    formas.push({ t: 'point', p: v, role: 'given' });
  });
  formas.push(titulo(`${conta(n, 'lado')}: soma dos internos = ${g(soma)}`, 132));
  formas.push(
    titulo(
      Number.isInteger(cada)
        ? `Cada ângulo interno = ${g(soma)} ÷ ${n} = ${g(cada)}`
        : `Cada ângulo interno = ${g(soma)} ÷ ${n} ≈ ${g1(cada)}`,
      -135,
    ),
  );
  return scene(formas, { a11y, viewBox: QUADRO_EXPLORADOR });
}

// ---------------------------------------------------------------------------
// Animação: ângulos externos de três polígonos
// ---------------------------------------------------------------------------

const R_ANIM = 60;
const NOMES_ANIM: readonly [number, string][] = [
  [3, 'Triângulo'],
  [4, 'Quadrilátero'],
  [5, 'Pentágono'],
];
const CENTROS_X = [-225, 0, 225];

function externosDe(vs: readonly Vec2[], s: number, k0: number): Shape[] {
  const n = vs.length;
  const G = centroide(vs);
  const w = vs.map((v) => ({ x: G.x + s * (v.x - G.x), y: G.y + s * (v.y - G.y) }));
  const raio = 0.45 * R_ANIM;
  const out: Shape[] = [];

  for (let k = 0; k < n; k++) {
    const antes = vs[(k - 1 + n) % n] as Vec2;
    const aqui = vs[k] as Vec2;
    const depois = vs[(k + 1) % n] as Vec2;
    const V = w[k] as Vec2;
    const a0 = Math.atan2(aqui.y - antes.y, aqui.x - antes.x);
    let a1 = Math.atan2(depois.y - aqui.y, depois.x - aqui.x);
    while (a1 < a0) a1 += 2 * Math.PI;
    const pts: Vec2[] = [V];
    for (let i = 0; i <= 16; i++) {
      const a = a0 + ((a1 - a0) * i) / 16;
      pts.push({ x: V.x + raio * Math.cos(a), y: V.y + raio * Math.sin(a) });
    }
    out.push({
      t: 'poly',
      pts,
      closed: true,
      role: 'result',
      fill: (k + k0) % 2 === 0 ? 'result-fill' : 'given-fill',
    });
    // O prolongamento do lado — de onde se mede o giro — some enquanto a figura encolhe.
    if (s > 0.55) {
      out.push({
        t: 'seg',
        a: V,
        b: { x: V.x + 1.3 * raio * Math.cos(a0), y: V.y + 1.3 * raio * Math.sin(a0) },
        role: 'construction',
      });
    }
  }
  if (s > 0.02) out.push({ t: 'poly', pts: w, closed: true, role: 'given' });
  else out.push({ t: 'point', p: G, role: 'given' });
  return out;
}

/**
 * Quadro no instante `t` ∈ [0, 1]. Os ângulos externos já começam traçados; entre 12% e
 * 72% os polígonos se contraem até a escala ZERO; depois, a pausa.
 *
 * O TEXTO NÃO MUDA E NÃO DÁ A RESPOSTA. Uma versão anterior trocava a frase a cada fase
 * e terminava anunciando "360°" — mas é a própria animação que deve levar o aluno a
 * perceber a volta completa. A frase só dirige o olhar para o que importa observar.
 */
export function animacaoExternos(t: number, a11y: { title: string; desc: string }): Scene {
  const q = clamp01(t);
  const s = 1 - suave((q - 0.12) / 0.6);
  const formas: Shape[] = [];
  NOMES_ANIM.forEach(([n, nome], i) => {
    const cx = CENTROS_X[i] as number;
    const vs = poligonoIrregular(n, R_ANIM, { x: cx, y: 5 });
    formas.push(...externosDe(vs, s, i));
    formas.push({
      t: 'label',
      at: { x: cx, y: -92 },
      text: [{ t: 'text', s: nome }],
      anchor: 'c',
      emphasis: 'axis',
    });
  });
  formas.push(titulo(FRASE_EXTERNOS, 100));
  return scene(formas, { a11y, viewBox: { x: -320, y: -112, w: 640, h: 232 } });
}

export const FRASE_EXTERNOS =
  'Repare que os ângulos externos de cada polígono não se alteram quando ele se contrai.';
